// Component: LiveDataStatus.jsx
// Purpose: #44 — the ONE place the app admits its live data has stopped.
//
//   The whole product is live by design, so "live" is the default and is never
//   marked. Three tiers, one source of truth (connectionHealth/hasConnectedOnce):
//     1. steady healthy        -> nothing renders (never a permanently-green liar)
//     2. outage                -> the pill below (catches you when you come back
//        to the desk; a toast is long gone by then - the #44 gap). Sustained +
//        tab hidden additionally raises a browser notification.
//     3. after a VISIBLE outage ends -> a brief quiet static "Reconnected" line
//        in the pill's spot for RECOVERED_VISIBLE_MS, which vanishes on its own.
//        Never a toast/modal/notification, never pulsing. Re-degrading replaces
//        it at once; an unmount clears its timer.
//   It is mounted once in the Navbar, so every page is covered with no per-page work.

import React, { useContext, useEffect, useRef, useState } from "react";
import { WebSocketContext } from "../context/WebSocketProvider";
import { Modal } from "./ds";
import WebSocketManager from "../websocket/WebSocketManager";

/*>>> Per-state copy + severity. `warn` = still auto-recovering on its own,
     `error` = it has been down long enough that you should know. <<<*/
const STATE_META = {
  reconnecting: {
    severity: "warn",
    pill: "Reconnecting",
    title: "Reconnecting to live data",
    body: "Live prices and order updates have paused. We're reconnecting automatically — you don't need to do anything.",
  },
  stalled: {
    severity: "warn",
    pill: "Data paused",
    title: "Live data has stopped updating",
    //! V12b: this used to promise "We're restoring it automatically" for the whole
    //! outage. Two things make that untrue: the self-heal is BOUNDED (a few
    //! reconnects, then it stops — a browser reconnect cannot revive a dead feed
    //! upstream), and for most of a real stall the cause is upstream anyway. A
    //! banner that keeps promising an active recovery that has already given up
    //! is the same class of lie as the pill that said "Live" over frozen prices.
    body: "The connection is open but no price updates are arriving — usually the market data feed upstream has stopped. We retry a few times automatically; prices on screen may be out of date until it returns.",
  },
  offline: {
    severity: "warn",
    pill: "You're offline",
    title: "You appear to be offline",
    body: "Live data is paused while your device has no connection. It will resume automatically once you're back online.",
  },
  degraded: {
    severity: "error",
    pill: "Live data down",
    title: "Live data isn't updating",
    body: "We've been unable to reconnect for a while and are still retrying in the background. Prices on screen may be out of date — refreshing often clears it.",
  },
};

//! Sustained outage before we escalate to a browser notification.
const NOTIFY_AFTER_MS = 45000;
//* How long the recovery line stays before vanishing by itself.
const RECOVERED_VISIBLE_MS = 5000;
//! Claims TRANSPORT only: connected = re-auth done, NOT proven tick delivery.
//! "Live data restored" was rejected (critic F2) as an over-claim.
const RECOVERED_COPY = "Reconnected";

/*>>> "14:32" for the last time real data arrived, or null if none this session. <<<*/
//! V12b F5: during a STALL this must read the last TICK, not the last data frame.
//! lastInboundDataAt is stamped by ANY data frame, and orders/positions/portfolio/
//! OHLC are ALWAYS_STREAM — they keep flowing while ticks are dead. Preferring it
//! unconditionally made the modal say "Data last received at «seconds ago»" while
//! the pill that opened it said "Data paused" — two contradictory claims on the
//! same decision surface. Only reachable since the banner started persisting.
function lastUpdatedLabel(connectionHealth) {
  const ts = connectionHealth === "stalled"
    ? WebSocketManager.lastInboundTickAt
    : (WebSocketManager.lastInboundDataAt || WebSocketManager.lastInboundTickAt);
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return null;
  }
}

export default function LiveDataStatus() {
  //! Read the context DIRECTLY, not via useWebSocketContext() — that hook throws
  //! without a provider, and a component whose only job is to report trouble must
  //! never be able to take the navbar down with it. No provider => stay silent.
  const ctx = useContext(WebSocketContext);
  const connectionHealth = ctx?.connectionHealth;
  const hasConnectedOnce = ctx?.hasConnectedOnce;

  const [open, setOpen] = useState(false);
  const [notifyPermission, setNotifyPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  const healthy = !connectionHealth || connectionHealth === "connected";
  //! Suppress the first-load flash: a socket that is merely still connecting is not
  //! news, so stay quiet until one has existed at least once. Two exceptions, both
  //! cases where silence would BE the bug:
  //!   'offline'  — meaningful before any connect; the device has no network.
  //!   'degraded' — the fast-retry budget is spent (~3.5min of failure). Without
  //!                this, an app opened while the backend is down showed NOTHING,
  //!                for ever — which is precisely the #44 silent screen-blank,
  //!                just relocated to first load. Found in the review round.
  const alwaysWorthSaying = connectionHealth === "offline" || connectionHealth === "degraded";
  const suppressed = !hasConnectedOnce && !alwaysWorthSaying;
  const visible = !healthy && !suppressed;

  //--> Recovery still closes the modal; the pill owns in-app connection status.
  const wasHealthy = useRef(true);
  useEffect(() => {
    if (visible && wasHealthy.current) {
      wasHealthy.current = false;
    } else if (healthy && !wasHealthy.current) {
      wasHealthy.current = true;
      setOpen(false); // nothing left to explain
    }
  }, [visible, healthy]);

  //--> Tier 3: a sustained outage while the tab is in the background. Fires at
  //--> most once per outage, and only if permission was already granted.
  const notifiedThisOutage = useRef(false);
  useEffect(() => {
    if (!visible) {
      notifiedThisOutage.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      if (
        notifiedThisOutage.current ||
        typeof Notification === "undefined" ||
        Notification.permission !== "granted" ||
        !document.hidden //! you're looking at the page — the pill already says it
      ) {
        return;
      }
      notifiedThisOutage.current = true;
      try {
        const n = new Notification("DeeTrade — live data stopped", {
          body: "Prices and order updates aren't arriving. Reconnecting in the background.",
          tag: "deetrade-live-data", //! collapse, never stack
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch (e) {
        /* notification failed — the in-app pill still stands */
      }
    }, NOTIFY_AFTER_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  //--> Tier 2b: recovery line. Armed only when a pill was actually shown, so first
  //--> connect and suppressed outages never produce it. Timer is effect-scoped:
  //--> re-degrade or unmount runs the cleanup (no setState after unmount).
  const [recovered, setRecovered] = useState(false);
  const wasVisible = useRef(false);
  useEffect(() => {
    if (visible) {
      wasVisible.current = true;
      setRecovered(false);
      return undefined;
    }
    if (healthy && wasVisible.current) {
      wasVisible.current = false;
      setRecovered(true);
      const timer = setTimeout(() => setRecovered(false), RECOVERED_VISIBLE_MS);
      return () => clearTimeout(timer);
    }
    //! unhealthy-but-hidden (suppressed) or any other frame: never leave a timer-less line armed
    setRecovered(false);
    return undefined;
  }, [visible, healthy]);

  if (!visible && recovered && healthy) {
    return (
      <span
        role="status"
        data-testid="live-data-recovered"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          color: "color-mix(in srgb, var(--color-profit, #16a34a) 55%, var(--fg-1))",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "var(--color-profit, #16a34a)",
            flex: "0 0 auto",
          }}
        />
        {RECOVERED_COPY}
      </span>
    );
  }

  if (!visible) return null;

  const meta = STATE_META[connectionHealth] || STATE_META.reconnecting;
  const isError = meta.severity === "error";
  const accent = isError ? "var(--color-loss)" : "var(--warning)";
  //! --warning/--color-loss are theme-INVARIANT (defined once, used by both), and raw
  //! amber #f59e0b as TEXT measures 1.97:1 on the warm off-white canvas — fine on the
  //! dark canvas (~8:1), near-invisible in light. Mixing the accent toward --fg-1
  //! self-corrects per theme, since --fg-1 flips dark<->light with the theme.
  //! 55% is the least shift that still clears WCAG AA 4.5:1 for this 11px/600 text
  //! (measured in-browser: amber 4.74 light / 12.16 dark, loss 6.23 / 10.01) so the
  //! pill keeps its amber character. The dot and tint stay pure accent — shape, not text.
  const accentText = `color-mix(in srgb, ${accent} 55%, var(--fg-1))`;
  const lastUpdated = lastUpdatedLabel(connectionHealth);

  //! Permission is requested from a click inside the dialog — a real user
  //! gesture — never as a surprise prompt on page load.
  const requestNotifyPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setNotifyPermission(result);
    } catch (e) {
      setNotifyPermission("denied");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${meta.pill} — live data status`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          borderRadius: 20,
          border: "none",
          cursor: "pointer",
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          color: accentText,
          background: `color-mix(in srgb, ${accent} 14%, transparent)`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: accent,
            flex: "0 0 auto",
            animation: "lds-pulse 1.6s ease-in-out infinite",
          }}
        />
        {meta.pill}
        <style>{`@keyframes lds-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }`}</style>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="sm"
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
            {meta.title}
          </span>
        }
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} className="btn btn-secondary">
              Dismiss
            </button>
            <button type="button" onClick={() => window.location.reload()} className="btn btn-primary" autoFocus>
              Refresh now
            </button>
          </>
        }
      >
        <p className="ds-body" style={{ margin: "0 0 12px" }}>{meta.body}</p>
        {lastUpdated && (
          <p className="ds-body" style={{ margin: "0 0 12px" }}>
            Data last received at <strong>{lastUpdated}</strong>.
          </p>
        )}
        {notifyPermission === "default" && (
          <button
            type="button"
            onClick={requestNotifyPermission}
            className="btn btn-secondary"
            style={{ fontSize: 12 }}
          >
            Alert me if this happens while I'm on another tab
          </button>
        )}
      </Modal>
    </>
  );
}
